/**
 * @file PoCostSuppliedModal.js
 * @version 1.4.3
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-06
 * @modified 2026-10-21
 * @description Edit PO, narrowed (Cam, 6 Oct 2026): a PO's line cost prices
 *              and supplied quantities, and nothing else.
 *
 *   - A PO made in Xero: cost price only. Saving sends the new cost to Xero
 *     FIRST; if Xero refuses or cannot be reached, nothing is saved here and
 *     the reason is shown (PATCH /api/purchase-orders/:id/cost-and-supplied,
 *     services/poCostEditService on the server).
 *   - A PO-D whose customer order is Delivered: cost price and supplied
 *     quantity. A PO-H whose order is Delivered: supplied quantity only (a
 *     PO-H carries no cost — the haulier's charge arrives as its bill).
 *   - A stock PO-D (no customer order): its supplied quantity
 *     (`mode="supplied"`, from Search POs' supplied action).
 *
 * Everything else on the PO is shown, disabled, with the reason in its title.
 * The ordered quantity stays as ordered. A supplied quantity is the order
 * line's own wherever the PO line was bought for one (one source of truth with
 * Add Supplied Qty); a stock line keeps its own.
 *
 * 1.1.0 (2026-10-06, review): the slide-out buttons are measured from the
 * body (PoCostSuppliedBody — Modal portals on a second render, so the outer
 * component's hook ran before they existed); no cost price is shown or
 * editable without costs.view; a delivered PO-D whose day has passed offers
 * "Correct transport" — the full editor, whose save is allowed when only
 * haulier / driver / vehicle change (since 1.4.0 "Open the editor").
 *
 * 1.4.0 (2026-10-09, Cam): a delivered order's PO-D can be corrected in the
 * full editor again (words, item codes, supplier, terms, transport), so this
 * window offers "Open the editor" whatever the day, instead of "Correct
 * transport" once the day had passed.
 *
 * 1.2.0 (2026-10-06, Cam): Xero holds the PO as BILLED and will not take the
 * cost (409 XERO_PO_BILLED_IN_XERO): nothing is saved, and the window asks the
 * person to change the cost in Xero, with the lines from / to, and offers
 * "I've changed it in Xero — update from Xero" (POST
 * /api/purchase-orders/:id/resync-cost-from-xero, the costs they entered sent
 * for comparison). Xero's cost is taken; where it is not what they entered,
 * or Xero's quantities or lines differ, the window says so.
 * 1.2.1 (review): the resync is sent with confirm — the person has just
 * changed the cost in Xero on purpose, so no second question about a bill's
 * cost being replaced.
 * 1.3.0 (2026-10-08, Cam): a supplied quantity on an order whose invoice was
 * raised on the old quantity and has not gone out: the server offers the
 * invoice changes (409 INVOICE_CHANGES_PROPOSED) — InvoiceProposalDialog,
 * "Apply these changes and save" or "Continue editing" (nothing saved). What
 * happened to the invoice is said before the window closes.
 *
 * 1.4.2 (2026-10-20, Cam): ordered and supplied quantities show in the line's
 * own unit with their tonnes (QtyWithTonnes), and a supplied quantity typed
 * for a pack line says its tonnes under the input.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import { DecimalInput } from './OrderLinesGrid';
import { formatCurrency, formatPrice } from '../utils/currencyFormatter';
import { lineQtyInput } from '../utils/poQtyDisplay';
import QtyWithTonnes from './QtyWithTonnes';
import { quantityWithTonnes, qtyFactsOf } from '../utils/qtyWithTonnes';
import { formatDateShort } from '../utils/dateFormatters';
import { useAccess } from '../hooks/useAccess';
import './PoCostSuppliedModal.css';
import AlertDialog from './AlertDialog';
import InvoiceProposalDialog from './InvoiceProposalDialog';
import { isInvoiceQuestion, applyAnswer, invoiceUpdateMessage } from '../utils/invoiceProposals';

/**
 * A line counted in packs ("× 25kg bag"), a PO-H line and a haulage line
 * (pallets) show a count — "40", not "40.00" — read-only and in the input
 * alike (utils/poQtyDisplay.isCountedLine).
 */
// A None line (unit weight 0 — no weight) is a plain count too (utils/lineUnits).
const qtyInput = (r) => (value) => lineQtyInput(value, { kind: r.kind, unitSuffix: r.unitSuffix, unitWeightKg: r.unitWeightKg });

const num = (v) => {
  const n = parseFloat(String(v ?? '').replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};
const round4 = (n) => Math.round((Number(n) || 0) * 10000) / 10000;

/** The server's utils/billXeroPayload.poLineKey. */
function poLineKey(li, index) {
  if (!li || typeof li !== 'object') return `idx:${index}`;
  if (li.kind === 'haulage' && li.haulageForOrderLineId != null) return `haulage:${li.haulageForOrderLineId}`;
  if (li.orderLineId != null && li.orderLineId !== '') return `ol:${li.orderLineId}`;
  if (li.lineItemID) return `li:${li.lineItemID}`;
  return `idx:${index}`;
}

/** The window's body: measures the footer's slide-out buttons once they exist. */
function PoCostSuppliedBody({ deps, children }) {
  useSlideOutButton(deps);
  // The padding is here, not on .modal-body (the scroller): a padded scroller
  // holds the sticky table header below its padding and rows show above it
  // (Cam, 21 Oct 2026).
  return <div className="po-cost-supplied-body">{children}</div>;
}


/**
 * @param {{ purchaseOrderId: string, mode?: 'scope'|'supplied', onClose: () => void, onSaved?: (result) => void,
 *   onFullEdit?: (purchaseOrderId: string) => void }} props — onFullEdit opens the full editor (transport correction)
 *        purchaseOrderId — the PO's xero_purchase_order_id (incl. LOCAL-/TEMP-)
 */
function PoCostSuppliedModal({ purchaseOrderId, mode = 'scope', onClose, onSaved, onFullEdit }) {
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // The server's invoice question (2026-10-08), and what it said after.
  const [invoiceQuestion, setInvoiceQuestion] = useState(null);
  const [doneNote, setDoneNote] = useState(null);
  const [costs, setCosts] = useState({});      // key -> typed cost
  const [supplied, setSupplied] = useState({}); // key -> typed supplied
  const [reason, setReason] = useState('');
  // Billed in Xero and refused: { message, lines, intended } — the prompt.
  const [billed, setBilled] = useState(null);
  const [resyncing, setResyncing] = useState(false);
  // The resync's answer: { messages, warnings, differences }.
  const [resynced, setResynced] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Cost is a permission (costs.view): without it no cost is shown or changed.
  const { can } = useAccess();
  const seeCosts = can('costs.view');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/purchase-orders/${encodeURIComponent(purchaseOrderId)}`, { credentials: 'include' });
        const data = await res.json();
        if (cancelled) return;
        if (data.success) setPo(data.purchaseOrder);
        else setError(data.error || 'Could not load the purchase order');
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [purchaseOrderId, reloadKey]);

  const haulage = po?.poType === 'haulage';
  const scope = po?.costEdit || null;
  // What may change here (the server decides the same — services/poCostEditService).
  const canCost = !!po && !haulage && seeCosts && (mode === 'supplied' ? false : (scope ? scope.cost : !po.xeroCostOnly));
  const showCost = !haulage && seeCosts;
  // A delivered PO-D: its words, item codes, supplier, terms and (once the
  // day has passed) transport are corrected in the full editor (2026-10-09).
  const transportFix = !!onFullEdit && !!po && scope?.mode === 'delivered' && !haulage;
  const canSupplied = !!po && !po.xeroCostOnly && (scope ? scope.supplied : true);
  const fixedReason = scope?.reason
    || (mode === 'supplied' ? 'Only the supplied quantity is recorded here' : 'Only the cost price and supplied quantities change here');
  const needsReason = !!po?.editNeedsReason && canCost;

  const rows = useMemo(() => {
    if (!po) return [];
    if (haulage) {
      return (po.haulageLines || []).map((l, i) => ({
        key: l.suppliedKey || `h:${l.sourceOrderLineId}`, i, description: l.description, itemCode: l.itemCode,
        kind: 'po_h', quantity: l.quantity, unitSuffix: l.unitSuffix, supplied: l.suppliedQuantity, suppliedBlock: l.suppliedBlock, cost: null,
        // Its unit weight (stored, else weight ÷ quantity) for the tonnes.
        facts: { unitWeightKg: Number(l.unitWeightKg) > 0 ? Number(l.unitWeightKg)
          : (Number(l.weightKg) > 0 && Number(l.quantity) > 0 ? Math.round((l.weightKg / l.quantity) * 1000) / 1000 : null) }
      }));
    }
    return (po.lineItems || []).map((l, i) => ({
      key: l.suppliedKey || poLineKey(l, i), i, description: l.description, itemCode: l.itemCode, quantity: l.quantity,
      kind: l.kind || null, unitSuffix: l.unitSuffix, unitWeightKg: l.unitWeightKg ?? null,
      supplied: l.suppliedQuantity, suppliedBlock: l.suppliedBlock, cost: l.unitAmount,
      haulageLine: l.kind === 'haulage', lineAmount: l.lineAmount,
      // The quantity with its tonnes (utils/qtyWithTonnes).
      facts: { ...qtyFactsOf(l), pallets: null }
    }));
  }, [po, haulage]);

  const changes = useMemo(() => {
    const out = [];
    for (const r of rows) {
      const line = { key: r.key };
      if (canCost && !r.haulageLine && costs[r.key] !== undefined) {
        const c = num(costs[r.key]);
        if (c !== null && round4(c) !== round4(r.cost)) line.unitAmount = round4(c);
      }
      if (canSupplied && !r.haulageLine && !r.suppliedBlock && supplied[r.key] !== undefined) {
        const typed = String(supplied[r.key] ?? '').trim();
        const s = typed === '' ? null : num(typed);
        const was = r.supplied == null ? null : round4(r.supplied);
        if ((typed === '' || s !== null) && (s === null ? null : round4(s)) !== was) line.suppliedQuantity = s === null ? null : round4(s);
      }
      if (Object.keys(line).length > 1) out.push(line);
    }
    return out;
  }, [rows, costs, supplied, canCost, canSupplied]);

  const save = async (invoiceAnswer = null) => {
    if (!changes.length) { onClose(); return; }
    if (needsReason && !reason.trim()) { setError('This PO is billed — say in one line why its cost is being changed.'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/purchase-orders/${encodeURIComponent(purchaseOrderId)}/cost-and-supplied`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lines: changes,
          ...(needsReason ? { edit_reason: reason.trim() } : {}),
          ...(invoiceAnswer ? { invoice_changes: invoiceAnswer } : {})
        })
      });
      const data = await res.json();
      // An invoice raised on the old supplied quantity: its changes, to apply or go back.
      if (isInvoiceQuestion(res.status, data)) {
        setInvoiceQuestion({ proposals: data.proposals || [], notices: data.notices || [], stale: !!data.stale });
        return;
      }
      if (!res.ok || !data.success) {
        if (data.code === 'XERO_PO_BILLED_IN_XERO') {
          setBilled({ message: data.error, lines: data.billed_in_xero?.lines || [], intended: changes.filter(c => c.unitAmount !== undefined) });
          setResynced(null);
          return;
        }
        setError(data.error || 'Not saved');
        return;
      }
      onSaved && onSaved(data);
      const note = invoiceUpdateMessage(data.invoice_update);
      if (note) { setDoneNote({ message: note, type: data.invoice_update.refused?.length ? 'warning' : 'success' }); return; }
      onClose();
    } catch (err) {
      setError(`Not saved: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // "I've changed it in Xero": take Xero's cost back (only the cost).
  const resyncFromXero = async () => {
    setResyncing(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/purchase-orders/${encodeURIComponent(purchaseOrderId)}/resync-cost-from-xero`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lines: (billed?.intended || []).map(c => ({ key: c.key, unitAmount: c.unitAmount })), confirm: true })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'The cost could not be taken from Xero');
        return;
      }
      setBilled(null);
      setCosts({});
      setResynced({ messages: data.messages || [], warnings: data.warnings || 0, differences: (data.differences || []).length });
      setReloadKey(k => k + 1);
    } catch (err) {
      setError(`The cost could not be taken from Xero: ${err.message}`);
    } finally {
      setResyncing(false);
    }
  };

  const title = po ? `Edit ${po.purchaseOrderNumber}` : 'Edit purchase order';
  const xeroNote = po?.xeroMade && canCost
    ? 'Made in Xero: a new cost is sent to Xero when you save, and nothing is saved here if Xero does not take it.'
    : null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      size="lg"
      className="po-cost-supplied"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose} disabled={saving || resyncing}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">{resynced ? 'Close' : 'Cancel'}</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={() => save()} disabled={saving || resyncing || loading || !po || !changes.length} data-write-action>
              <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
              <span className="btn-label">{saving ? (po?.xeroMade ? 'Sending to Xero…' : 'Saving…') : 'Save'}</span>
            </button>
          </div>
        </div>
      }
    >
      <PoCostSuppliedBody deps={[loading, saving, !!po, changes.length, !!billed, resyncing, !!resynced]}>
      {loading ? <LoadingSpinner /> : !po ? (
        <p className="po-cost-supplied-error">{error || 'Could not load the purchase order'}</p>
      ) : (
        <>
          <p className="po-cost-supplied-notice">
            <span className="material-symbols-rounded">lock</span>
            <span>{fixedReason}{xeroNote ? ` ${xeroNote}` : ''}</span>
          </p>
          {transportFix && (
            <div className="po-cost-supplied-transport">
              <span>Its line words, item codes, supplier, terms and transport are corrected in the PO editor.</span>
              <button className="btn-slideout" onClick={() => { onClose(); onFullEdit(po.purchaseOrderID || purchaseOrderId); }}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">edit</span></div>
                <span className="btn-label">Open the editor</span>
              </button>
            </div>
          )}

          <div className="po-cost-supplied-fixed">
            {[
              ['Supplier', po.contactName || po.contact?.name || ''],
              ['Delivery date', po.deliveryDate ? formatDateShort(po.deliveryDate) : ''],
              ['Supplier terms', po.supplierDeliveryTerms || ''],
              ['Status', po.status || '']
            ].map(([label, value]) => (
              <label key={label} className="po-cost-supplied-field" title={fixedReason}>
                <span>{label}</span>
                <Input value={value} disabled title={fixedReason} onChange={() => {}} />
              </label>
            ))}
          </div>

          <div className="table-wrapper">
          <table className="data-table po-cost-supplied-lines">
            <thead>
              <tr>
                <th>Item</th>
                <th className="text-right">Ordered</th>
                <th className="text-right">Supplied</th>
                {showCost && <th className="text-right">Cost price</th>}
                {showCost && <th className="text-right">Line total</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map(r => {
                const costValue = costs[r.key] ?? r.cost ?? '';
                const typedCost = num(costs[r.key]);
                const lineTotal = typedCost !== null ? Math.round(typedCost * (Number(r.quantity) || 0) * 100) / 100 : r.lineAmount;
                const suppliedTitle = r.haulageLine ? 'Worked out from its order\'s haulage rate'
                  : !canSupplied ? fixedReason : (r.suppliedBlock || 'What was actually supplied (driver / weighbridge ticket)');
                return (
                  <tr key={r.key}>
                    <td>
                      {r.itemCode && <span className="po-cost-supplied-code">{r.itemCode}</span>}
                      {r.description || '—'}
                    </td>
                    {/* In the line's own unit with its tonnes, ordered and
                        supplied alike (utils/qtyWithTonnes — Cam, 20 Oct 2026). */}
                    <td className="text-right" title="Ordered — kept as ordered">
                      <QtyWithTonnes quantity={r.quantity} line={r.facts} />
                    </td>
                    <td className="text-right" title={suppliedTitle}>
                      {canSupplied && !r.haulageLine && !r.suppliedBlock ? (
                        <DecimalInput
                          min="0"
                          value={supplied[r.key] ?? (r.supplied ?? '')}
                          format={qtyInput(r)}
                          onValueChange={(v) => setSupplied(prev => ({ ...prev, [r.key]: v }))}
                          aria-label={`Supplied, ${r.description || 'line'}`}
                        />
                      ) : (r.supplied != null ? <QtyWithTonnes quantity={r.supplied} line={r.facts} /> : '—')}
                      {canSupplied && !r.haulageLine && !r.suppliedBlock && (() => {
                        const v = quantityWithTonnes(supplied[r.key] ?? r.supplied, r.facts);
                        return v.kind === 'packs' ? <span className="po-cost-supplied-tonnes">{v.tonnesText}</span> : null;
                      })()}
                    </td>
                    {showCost && (
                      <td className="text-right" title={r.haulageLine ? 'Worked out from its order\'s haulage rate' : (canCost ? 'Cost price per unit' : fixedReason)}>
                        {canCost && !r.haulageLine ? (
                          <DecimalInput
                            min="0"
                            value={costValue}
                            onValueChange={(v) => setCosts(prev => ({ ...prev, [r.key]: v }))}
                            aria-label={`Cost price, ${r.description || 'line'}`}
                          />
                        ) : formatPrice(r.cost)}
                      </td>
                    )}
                    {showCost && <td className="text-right">{formatCurrency(lineTotal)}</td>}
                  </tr>
                );
              }) : (
                <tr><td colSpan={showCost ? 5 : 3} className="no-items">No lines</td></tr>
              )}
            </tbody>
          </table>
          </div>

          {needsReason && (
            <label className="po-cost-supplied-field po-cost-supplied-reason">
              <span>Why is the cost changing? (it is billed)</span>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
          )}
          {billed && (
            <div className="po-cost-supplied-billed" role="alert">
              <p className="po-cost-supplied-billed-head">
                <span className="material-symbols-rounded">receipt_long</span>
                <span>Nothing was saved here.</span>
              </p>
              <p>{billed.message}</p>
              <div className="po-cost-supplied-billed-action">
                <button className="btn-slideout" onClick={resyncFromXero} disabled={resyncing} data-write-action
                  title="Once the cost is changed on the PO in Xero: take Xero's cost onto this PO (the cost only)">
                  <div className="icon-wrapper"><span className="material-symbols-rounded">sync</span></div>
                  <span className="btn-label">{resyncing ? 'Reading Xero…' : "I've changed it in Xero — update from Xero"}</span>
                </button>
              </div>
            </div>
          )}
          {resynced && (
            <div className={`po-cost-supplied-resynced${resynced.warnings || resynced.differences ? ' has-warnings' : ''}`} role="status">
              {resynced.messages.map((m, i) => <p key={i}>{m}</p>)}
            </div>
          )}
          {error && <p className="po-cost-supplied-error" role="alert">{error}</p>}
        </>
      )}
      </PoCostSuppliedBody>
      {invoiceQuestion && (
        <InvoiceProposalDialog
          proposals={invoiceQuestion.proposals}
          notices={invoiceQuestion.notices}
          stale={invoiceQuestion.stale}
          busy={saving}
          onApply={() => { const q = invoiceQuestion; setInvoiceQuestion(null); save(applyAnswer(q.proposals)); }}
          onContinue={() => setInvoiceQuestion(null)}
        />
      )}
      <AlertDialog
        isOpen={!!doneNote}
        type={doneNote?.type || 'info'}
        message={doneNote?.message || ''}
        onClose={() => { setDoneNote(null); onClose(); }}
      />
    </Modal>
  );
}

export default PoCostSuppliedModal;
